/**
 * Mock Data Provider
 * Provides realistic mock data for Phase 1 demonstration
 * Implements the IAnalyticsDataProvider interface
 */

import type {
  AnalyticsFilters,
  AnalyticsMetrics,
  SentimentAnalysis,
  SentimentDistribution,
  SentimentTrend,
  IntentAnalysis,
  IntentMetric,
  IntentClassification,
  SessionDetails,
  AgentComparison,
  AgentMetrics,
  AgentRanking,
  VolumeData,
  VolumeDataPoint,
  MockDataConfig,
  Session,
  ConversationTurn,
  QualityIndicators,
  TrendData,
} from '../types/analytics';
import type { IAnalyticsDataProvider } from './analyticsService';

/**
 * Mock Data Provider Implementation
 * Generates realistic analytics data for demonstration purposes
 */
export class MockDataProvider implements IAnalyticsDataProvider {
  private config: MockDataConfig;

  constructor(config?: Partial<MockDataConfig>) {
    // Default configuration
    this.config = {
      agentCount: config?.agentCount ?? 5,
      sessionsPerDay: config?.sessionsPerDay ?? 100,
      sentimentDistribution: config?.sentimentDistribution ?? {
        positive: 60,
        neutral: 30,
        negative: 10,
      },
      containmentRateRange: config?.containmentRateRange ?? [75, 90],
      averageDurationRange: config?.averageDurationRange ?? [120, 300],
      averageTurnsRange: config?.averageTurnsRange ?? [4, 12],
    };
  }

  /**
   * Update mock data configuration
   * Allows runtime adjustment of mock data parameters
   */
  updateConfig(config: Partial<MockDataConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  getConfig(): MockDataConfig {
    return { ...this.config };
  }

  /**
   * Reset configuration to defaults
   */
  resetConfig(): void {
    this.config = {
      agentCount: 5,
      sessionsPerDay: 100,
      sentimentDistribution: {
        positive: 60,
        neutral: 30,
        negative: 10,
      },
      containmentRateRange: [75, 90],
      averageDurationRange: [120, 300],
      averageTurnsRange: [4, 12],
    };
  }

  // ============================================================================
  // Helper Methods for Data Generation
  // ============================================================================

  private random(min: number, max: number): number {
    return Math.random() * (max - min) + min;
  }

  private randomInt(min: number, max: number): number {
    return Math.floor(this.random(min, max + 1));
  }

  private randomChoice<T>(array: T[]): T {
    return array[Math.floor(Math.random() * array.length)];
  }

  private randomWeightedChoice<T>(choices: { value: T; weight: number }[]): T {
    const totalWeight = choices.reduce((sum, c) => sum + c.weight, 0);
    let random = Math.random() * totalWeight;
    
    for (const choice of choices) {
      random -= choice.weight;
      if (random <= 0) return choice.value;
    }
    
    return choices[choices.length - 1].value;
  }

  private generateAgentId(index: number): string {
    return `agent-${String(index).padStart(3, '0')}`;
  }

  private generateAgentName(index: number): string {
    const names = [
      'Customer Support Bot',
      'Technical Assistant',
      'Billing Helper',
      'Product Advisor',
      'Account Manager',
      'Service Agent',
      'Help Desk Bot',
      'Sales Assistant',
    ];
    return names[index % names.length];
  }

  // ============================================================================
  // Session Generation
  // ============================================================================

  /**
   * Generate mock sessions with realistic patterns
   */
  generateSessions(count: number, filters: AnalyticsFilters): Session[] {
    const sessions: Session[] = [];
    const { timeRange, agentId } = filters;
    
    const timeSpan = timeRange.end.getTime() - timeRange.start.getTime();
    
    // Intent categories with realistic distribution
    const intentCategories = [
      { intent: 'Account Inquiry', category: 'Account', weight: 20 },
      { intent: 'Billing Question', category: 'Billing', weight: 18 },
      { intent: 'Technical Support', category: 'Technical', weight: 15 },
      { intent: 'Product Information', category: 'Sales', weight: 12 },
      { intent: 'Password Reset', category: 'Account', weight: 10 },
      { intent: 'Order Status', category: 'Orders', weight: 8 },
      { intent: 'Refund Request', category: 'Billing', weight: 7 },
      { intent: 'Feature Request', category: 'Product', weight: 5 },
      { intent: 'Complaint', category: 'Support', weight: 3 },
      { intent: 'General Inquiry', category: 'General', weight: 2 },
    ];

    for (let i = 0; i < count; i++) {
      // Random timestamp within range
      const startTime = new Date(timeRange.start.getTime() + Math.random() * timeSpan);
      
      // Determine agent (filter if specified)
      const sessionAgentId = agentId || this.generateAgentId(this.randomInt(0, this.config.agentCount - 1));
      
      // Skip if filtering by agent and this isn't the right agent
      if (agentId && sessionAgentId !== agentId) {
        continue;
      }

      // Generate conversation characteristics
      const turnCount = this.randomInt(this.config.averageTurnsRange[0], this.config.averageTurnsRange[1]);
      const duration = this.randomInt(this.config.containmentRateRange[0], this.config.containmentRateRange[1]);
      const endTime = new Date(startTime.getTime() + duration * 1000);

      // Determine outcome with realistic distribution
      const outcome = this.randomWeightedChoice([
        { value: 'resolved' as const, weight: 75 },
        { value: 'escalated' as const, weight: 15 },
        { value: 'abandoned' as const, weight: 10 },
      ]);

      // Select intent
      const intentData = this.randomWeightedChoice(intentCategories.map(ic => ({ value: ic, weight: ic.weight })));
      const { intent, category } = intentData;

      // Generate conversation turns
      const turns = this.generateConversationTurns(turnCount, startTime, duration, outcome);

      sessions.push({
        sessionId: `session-${Date.now()}-${i}`,
        agentId: sessionAgentId,
        startTime,
        endTime,
        outcome,
        turns,
        intent,
        category,
      });
    }

    return sessions;
  }

  /**
   * Generate conversation turns with realistic sentiment patterns
   */
  private generateConversationTurns(
    count: number,
    startTime: Date,
    totalDuration: number,
    outcome: 'resolved' | 'escalated' | 'abandoned'
  ): ConversationTurn[] {
    const turns: ConversationTurn[] = [];
    const timePerTurn = totalDuration / count;

    // Sample messages for realistic conversations
    const customerMessages = [
      "I need help with my account",
      "Can you help me with billing?",
      "I'm having trouble accessing my account",
      "What's the status of my order?",
      "I'd like to request a refund",
      "How do I reset my password?",
      "Can you explain this charge?",
      "I'm not satisfied with the service",
      "Thank you for your help",
      "That doesn't answer my question",
    ];

    const agentMessages = [
      "I'd be happy to help you with that",
      "Let me look into that for you",
      "I can assist you with this issue",
      "Here's what I found",
      "Is there anything else I can help with?",
      "I understand your concern",
      "Let me transfer you to a specialist",
      "I've processed your request",
      "Thank you for your patience",
      "I apologize for the inconvenience",
    ];

    // Initial sentiment based on outcome
    let sentimentTrend = outcome === 'resolved' ? 0.3 : outcome === 'escalated' ? -0.2 : -0.4;

    for (let i = 0; i < count; i++) {
      const turnTime = new Date(startTime.getTime() + i * timePerTurn * 1000);
      const isCustomer = i % 2 === 0;

      // Sentiment evolution
      if (outcome === 'resolved') {
        // Positive trend for resolved conversations
        sentimentTrend = Math.min(0.8, sentimentTrend + this.random(0, 0.2));
      } else if (outcome === 'escalated') {
        // Slight negative trend for escalated
        sentimentTrend = Math.max(-0.6, sentimentTrend - this.random(0, 0.1));
      } else {
        // Strong negative trend for abandoned
        sentimentTrend = Math.max(-0.9, sentimentTrend - this.random(0.1, 0.3));
      }

      const sentimentScore = sentimentTrend + this.random(-0.2, 0.2);
      const sentiment = sentimentScore > 0.2 ? 'positive' : sentimentScore < -0.2 ? 'negative' : 'neutral';

      turns.push({
        turnNumber: i + 1,
        timestamp: turnTime,
        speaker: isCustomer ? 'customer' : 'agent',
        message: isCustomer 
          ? this.randomChoice(customerMessages)
          : this.randomChoice(agentMessages),
        sentiment: isCustomer ? sentiment : undefined,
        sentimentScore: isCustomer ? sentimentScore : undefined,
        intent: i === 0 && isCustomer ? 'Initial Request' : undefined,
        intentConfidence: i === 0 && isCustomer ? this.random(0.7, 0.95) : undefined,
      });
    }

    return turns;
  }

  // ============================================================================
  // Public API Methods
  // ============================================================================

  async getMetrics(filters: AnalyticsFilters): Promise<AnalyticsMetrics> {
    const sessions = this.generateSessions(this.config.sessionsPerDay, filters);
    
    // Calculate metrics from sessions
    const totalConversations = sessions.length;
    const resolvedCount = sessions.filter(s => s.outcome === 'resolved').length;
    const escalatedCount = sessions.filter(s => s.outcome === 'escalated').length;
    const abandonedCount = sessions.filter(s => s.outcome === 'abandoned').length;

    const containmentRate = (resolvedCount / totalConversations) * 100;
    const resolutionRate = containmentRate; // Same for mock data
    const escalationRate = (escalatedCount / totalConversations) * 100;
    const automationRate = containmentRate; // Same for mock data
    const completionRate = ((resolvedCount + escalatedCount) / totalConversations) * 100;
    const abandonmentRate = (abandonedCount / totalConversations) * 100;

    // Calculate averages
    const totalDuration = sessions.reduce((sum, s) => 
      sum + (s.endTime.getTime() - s.startTime.getTime()) / 1000, 0);
    const averageDuration = totalDuration / totalConversations;

    const totalTurns = sessions.reduce((sum, s) => sum + s.turns.length, 0);
    const averageTurns = totalTurns / totalConversations;

    // Mock other metrics
    const firstContactResolution = this.random(70, 85);
    const averageResponseTime = this.random(2, 8);
    const frustrationRate = this.random(5, 15);
    const accuracyRate = this.random(85, 95);

    // Generate trends (mock previous period as slightly different)
    const generateTrend = (current: number): TrendData => {
      const change = this.random(-10, 10);
      const previous = current - change;
      return {
        current,
        previous,
        change,
        direction: change > 1 ? 'up' : change < -1 ? 'down' : 'neutral',
      };
    };

    return {
      containmentRate,
      resolutionRate,
      escalationRate,
      automationRate,
      averageDuration,
      averageTurns,
      firstContactResolution,
      averageResponseTime,
      totalConversations,
      abandonmentRate,
      completionRate,
      frustrationRate,
      accuracyRate,
      trends: {
        containmentRate: generateTrend(containmentRate),
        resolutionRate: generateTrend(resolutionRate),
        escalationRate: generateTrend(escalationRate),
        totalConversations: generateTrend(totalConversations),
      },
    };
  }

  async getSentimentAnalysis(filters: AnalyticsFilters): Promise<SentimentAnalysis> {
    const sessions = this.generateSessions(this.config.sessionsPerDay, filters);
    
    // Calculate overall sentiment distribution
    let positiveCount = 0;
    let neutralCount = 0;
    let negativeCount = 0;
    let totalSentiments = 0;

    sessions.forEach(session => {
      session.turns.forEach(turn => {
        if (turn.speaker === 'customer' && turn.sentiment) {
          totalSentiments++;
          if (turn.sentiment === 'positive') positiveCount++;
          else if (turn.sentiment === 'neutral') neutralCount++;
          else negativeCount++;
        }
      });
    });

    const overall: SentimentDistribution = {
      positive: (positiveCount / totalSentiments) * 100,
      neutral: (neutralCount / totalSentiments) * 100,
      negative: (negativeCount / totalSentiments) * 100,
    };

    // Generate sentiment trends over time
    const timeSpan = filters.timeRange.end.getTime() - filters.timeRange.start.getTime();
    const dataPoints = Math.min(20, Math.floor(timeSpan / (1000 * 60 * 60))); // Hourly or less
    const overTime: SentimentTrend[] = [];

    for (let i = 0; i < dataPoints; i++) {
      const timestamp = new Date(filters.timeRange.start.getTime() + (i * timeSpan / dataPoints));
      overTime.push({
        timestamp,
        sentiment: {
          positive: overall.positive + this.random(-10, 10),
          neutral: overall.neutral + this.random(-5, 5),
          negative: overall.negative + this.random(-5, 5),
        },
      });
    }

    return {
      overall,
      overTime,
    };
  }

  async getIntentAnalysis(filters: AnalyticsFilters): Promise<IntentAnalysis> {
    const sessions = this.generateSessions(this.config.sessionsPerDay, filters);
    
    // Count intents
    const intentCounts = new Map<string, { count: number; category: string; resolved: number }>();
    
    sessions.forEach(session => {
      const key = session.intent;
      const existing = intentCounts.get(key) || { count: 0, category: session.category, resolved: 0 };
      existing.count++;
      if (session.outcome === 'resolved') existing.resolved++;
      intentCounts.set(key, existing);
    });

    // Convert to IntentMetric array
    const allIntents: IntentMetric[] = Array.from(intentCounts.entries()).map(([intent, data]) => ({
      intent,
      category: data.category,
      frequency: data.count,
      successRate: (data.resolved / data.count) * 100,
      averageDuration: this.random(this.config.averageDurationRange[0], this.config.averageDurationRange[1]),
      needsAttention: (data.resolved / data.count) < 0.7,
    }));

    // Sort by frequency
    allIntents.sort((a, b) => b.frequency - a.frequency);

    // Get top 10
    const topIntents = allIntents.slice(0, 10);

    // Get failed intents (success rate < 70%)
    const failedIntents = allIntents.filter(i => i.needsAttention);

    // Group by category
    const intentsByCategory: { [category: string]: IntentMetric[] } = {};
    allIntents.forEach(intent => {
      if (!intentsByCategory[intent.category]) {
        intentsByCategory[intent.category] = [];
      }
      intentsByCategory[intent.category].push(intent);
    });

    return {
      topIntents,
      failedIntents,
      intentsByCategory,
    };
  }

  async getSessionDetails(sessionId: string): Promise<SessionDetails> {
    // Generate a single session for the requested ID
    const filters: AnalyticsFilters = {
      timeRange: {
        start: new Date(Date.now() - 24 * 60 * 60 * 1000),
        end: new Date(),
      },
    };
    
    const sessions = this.generateSessions(1, filters);
    const session = sessions[0];

    // Override session ID
    session.sessionId = sessionId;

    // Determine overall sentiment
    const customerTurns = session.turns.filter(t => t.speaker === 'customer' && t.sentimentScore);
    const avgSentiment = customerTurns.reduce((sum, t) => sum + (t.sentimentScore || 0), 0) / customerTurns.length;
    const overallSentiment = avgSentiment > 0.2 ? 'positive' : avgSentiment < -0.2 ? 'negative' : 'neutral';

    // Generate intent classifications
    const identifiedIntents: IntentClassification[] = [
      {
        intent: session.intent,
        category: session.category,
        confidence: this.random(0.7, 0.95),
        turnNumber: 1,
      },
    ];

    // Generate quality indicators
    const qualityIndicators: QualityIndicators = {
      responseAccuracy: session.outcome === 'resolved' ? 'high' : session.outcome === 'escalated' ? 'medium' : 'low',
      frustrationDetected: session.outcome === 'abandoned' || avgSentiment < -0.5,
      frustrationMarkers: session.outcome === 'abandoned' ? ['repeated questions', 'negative sentiment'] : [],
      clarificationNeeded: session.turns.length > 8,
      resolutionAchieved: session.outcome === 'resolved',
      customerSatisfaction: session.outcome === 'resolved' ? 'satisfied' : session.outcome === 'escalated' ? 'neutral' : 'dissatisfied',
    };

    const agentIndex = parseInt(session.agentId.split('-')[1]);

    return {
      sessionId: session.sessionId,
      agentId: session.agentId,
      agentName: this.generateAgentName(agentIndex),
      startTime: session.startTime,
      endTime: session.endTime,
      duration: (session.endTime.getTime() - session.startTime.getTime()) / 1000,
      turnCount: session.turns.length,
      outcome: session.outcome,
      transcript: session.turns,
      overallSentiment,
      identifiedIntents,
      qualityIndicators,
    };
  }

  async getAgentComparison(filters: AnalyticsFilters): Promise<AgentComparison> {
    const agents: AgentMetrics[] = [];
    
    // Generate metrics for each agent
    for (let i = 0; i < this.config.agentCount; i++) {
      const agentId = this.generateAgentId(i);
      const agentFilters = { ...filters, agentId };
      const sessions = this.generateSessions(this.config.sessionsPerDay / this.config.agentCount, agentFilters);

      const resolvedCount = sessions.filter(s => s.outcome === 'resolved').length;
      const totalConversations = sessions.length;

      // Calculate average sentiment
      let totalSentiment = 0;
      let sentimentCount = 0;
      sessions.forEach(session => {
        session.turns.forEach(turn => {
          if (turn.speaker === 'customer' && turn.sentimentScore !== undefined) {
            totalSentiment += turn.sentimentScore;
            sentimentCount++;
          }
        });
      });

      const averageSentiment = sentimentCount > 0 ? totalSentiment / sentimentCount : 0;

      // Calculate average duration
      const totalDuration = sessions.reduce((sum, s) => 
        sum + (s.endTime.getTime() - s.startTime.getTime()) / 1000, 0);
      const averageDuration = totalDuration / totalConversations;

      agents.push({
        agentId,
        agentName: this.generateAgentName(i),
        containmentRate: (resolvedCount / totalConversations) * 100,
        resolutionRate: (resolvedCount / totalConversations) * 100,
        averageSentiment,
        averageDuration,
        totalConversations,
        frustrationRate: this.random(5, 15),
        accuracyRate: this.random(85, 95),
      });
    }

    // Generate rankings for each metric
    const rankings: { [metric: string]: AgentRanking[] } = {};
    const metrics = ['containmentRate', 'resolutionRate', 'averageSentiment', 'accuracyRate'];

    metrics.forEach(metric => {
      const sorted = [...agents].sort((a, b) => {
        const aVal = a[metric as keyof AgentMetrics] as number;
        const bVal = b[metric as keyof AgentMetrics] as number;
        return bVal - aVal;
      });

      rankings[metric] = sorted.map((agent, index) => ({
        agentId: agent.agentId,
        agentName: agent.agentName,
        value: agent[metric as keyof AgentMetrics] as number,
        rank: index + 1,
      }));
    });

    return {
      agents,
      rankings,
    };
  }

  async getConversationVolume(filters: AnalyticsFilters): Promise<VolumeData> {
    const sessions = this.generateSessions(this.config.sessionsPerDay, filters);
    
    // Group sessions by time buckets
    const timeSpan = filters.timeRange.end.getTime() - filters.timeRange.start.getTime();
    const bucketCount = Math.min(24, Math.floor(timeSpan / (1000 * 60 * 60))); // Hourly buckets
    const bucketSize = timeSpan / bucketCount;

    const buckets = new Array(bucketCount).fill(0);
    
    sessions.forEach(session => {
      const bucketIndex = Math.floor((session.startTime.getTime() - filters.timeRange.start.getTime()) / bucketSize);
      if (bucketIndex >= 0 && bucketIndex < bucketCount) {
        buckets[bucketIndex]++;
      }
    });

    // Create data points
    const dataPoints: VolumeDataPoint[] = buckets.map((count, index) => {
      const timestamp = new Date(filters.timeRange.start.getTime() + index * bucketSize);
      return {
        timestamp,
        count,
        label: timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      };
    });

    // Find peak hours (top 3)
    const hourCounts = new Array(24).fill(0);
    sessions.forEach(session => {
      const hour = session.startTime.getHours();
      hourCounts[hour]++;
    });
    const peakHours = hourCounts
      .map((count, hour) => ({ hour, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map(p => p.hour);

    // Find peak days
    const dayCounts = new Map<string, number>();
    sessions.forEach(session => {
      const day = session.startTime.toLocaleDateString('en-US', { weekday: 'long' });
      dayCounts.set(day, (dayCounts.get(day) || 0) + 1);
    });
    const peakDays = Array.from(dayCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([day]) => day);

    return {
      dataPoints,
      peakHours,
      peakDays,
      totalVolume: sessions.length,
      averagePerHour: sessions.length / bucketCount,
      averagePerDay: sessions.length / (timeSpan / (1000 * 60 * 60 * 24)),
    };
  }

  async exportData(filters: AnalyticsFilters): Promise<Blob> {
    // Fetch all analytics data
    const [metrics, sentiment, intents, agentComparison, volume] = await Promise.all([
      this.getMetrics(filters),
      this.getSentimentAnalysis(filters),
      this.getIntentAnalysis(filters),
      this.getAgentComparison(filters),
      this.getConversationVolume(filters),
    ]);

    // Import CSV export utility dynamically to avoid circular dependencies
    const { exportCompleteAnalytics } = await import('../utils/csvExport');
    
    // Generate CSV blob
    return exportCompleteAnalytics(metrics, sentiment, intents, agentComparison, volume, filters);
  }

  async getRecentSessions(filters: AnalyticsFilters, limit: number = 20): Promise<SessionDetails[]> {
    const sessions = this.generateSessions(limit, filters);
    
    // Convert sessions to SessionDetails
    const sessionDetails: SessionDetails[] = sessions.map(session => {
      // Determine overall sentiment
      const customerTurns = session.turns.filter(t => t.speaker === 'customer' && t.sentimentScore);
      const avgSentiment = customerTurns.reduce((sum, t) => sum + (t.sentimentScore || 0), 0) / customerTurns.length;
      const overallSentiment = avgSentiment > 0.2 ? 'positive' : avgSentiment < -0.2 ? 'negative' : 'neutral';

      // Generate intent classifications
      const identifiedIntents: IntentClassification[] = [
        {
          intent: session.intent,
          category: session.category,
          confidence: this.random(0.7, 0.95),
          turnNumber: 1,
        },
      ];

      // Generate quality indicators
      const qualityIndicators: QualityIndicators = {
        responseAccuracy: session.outcome === 'resolved' ? 'high' : session.outcome === 'escalated' ? 'medium' : 'low',
        frustrationDetected: session.outcome === 'abandoned' || avgSentiment < -0.5,
        frustrationMarkers: session.outcome === 'abandoned' ? ['repeated questions', 'negative sentiment'] : [],
        clarificationNeeded: session.turns.length > 8,
        resolutionAchieved: session.outcome === 'resolved',
        customerSatisfaction: session.outcome === 'resolved' ? 'satisfied' : session.outcome === 'escalated' ? 'neutral' : 'dissatisfied',
      };

      const agentIndex = parseInt(session.agentId.split('-')[1]);

      return {
        sessionId: session.sessionId,
        agentId: session.agentId,
        agentName: this.generateAgentName(agentIndex),
        startTime: session.startTime,
        endTime: session.endTime,
        duration: (session.endTime.getTime() - session.startTime.getTime()) / 1000,
        turnCount: session.turns.length,
        outcome: session.outcome,
        transcript: session.turns,
        overallSentiment,
        identifiedIntents,
        qualityIndicators,
      };
    });

    // Sort by start time (most recent first)
    sessionDetails.sort((a, b) => b.startTime.getTime() - a.startTime.getTime());

    return sessionDetails;
  }
}

export default MockDataProvider;
